import { IMasterStatus } from "../../../redux/status/status.types";
import { ILead, IMultipleEvent } from "../../../redux/leads/leads.types";

export const formatAdditionalContacts = (
  contacts?: {
    country_code?: string;
    contact_number?: string;
    name?: string;
    relation_with_customer?: string | null;
  }[],
) => {
  if (!Array.isArray(contacts) || contacts.length === 0) return [];

  return contacts
    .map((c) => {
      const phone = [c.country_code, c.contact_number]
        .filter(Boolean)
        .join(" ")
        .trim();
      const meta = [c.name?.trim(), c.relation_with_customer?.trim()]
        .filter(Boolean)
        .join(" - ");

      if (phone && meta) {
        return `${phone} (${meta})`;
      }
      return phone || meta || "";
    })
    .filter(Boolean);
};

//availibilty helper

export const isAvailabilityRelevantStatus = (status?: string) =>
  status && status !== "CANCELLED" && status !== "DEAD";

export const hasAvailabilityInputs = (event: any) =>
  Boolean(
    event.preferred_venue_uuid && event.function_date && event.event_time_uuid,
  );

export const hasAvailabilityFieldsChanged = (current: any, initial?: any) => {
  if (!initial) return true;

  return (
    current.preferred_venue_uuid !== initial.preferred_venue_uuid ||
    current.function_date !== initial.function_date ||
    current.event_time_uuid !== initial.event_time_uuid
  );
};

/**
 * Returns a dedup key if availability should be checked, empty string if not.
 * Covers: status relevance, BOOKED-transition logic, required inputs, field diff.
 * 
 * BOOKED skip rule: only skip if the event already existed in DB as BOOKED
 * (has a UUID and prev status was also BOOKED). New events or status transitions always check.
 */
export const getAvailabilityCheckKey = (current: any, prev: any): string => {
  if (!isAvailabilityRelevantStatus(current.actual_status)) return "";
  if (!hasAvailabilityInputs(current)) return "";

  const statusChanged = current.actual_status !== prev.actual_status;
  const isExistingEvent = Boolean(current.lead_event_uuid || current.event_uuid);
  const fieldsChanged = hasAvailabilityFieldsChanged(current, prev);

  // Skip ONLY if: existing event that was already BOOKED from DB AND nothing changed
  if (isExistingEvent && !statusChanged && !fieldsChanged) {
    // Was already in this status with same fields — no need to re-check
    if (current.actual_status === "BOOKED") return "";
  }

  // Something changed (status or fields) — check availability
  if (statusChanged || fieldsChanged) {
    return `${current.preferred_venue_uuid}_${current.function_date}_${current.event_time_uuid}`;
  }

  return "";
};

export const getLowestOngoingStatus = (
  statuses: IMasterStatus[],
): IMasterStatus | null => {
  if (!statuses || statuses.length === 0) return null;

  const ongoingStatuses = statuses.filter(
    (status: IMasterStatus) =>
      status.actual_status === "ONGOING" || status.name === "ONGOING",
  );

  if (ongoingStatuses.length === 0) return null;

  ongoingStatuses.sort((a, b) => {
    const idA = a.status_unique_id;
    const idB = b.status_unique_id;

    const hasIdA = idA !== null && idA !== undefined;
    const hasIdB = idB !== null && idB !== undefined;

    if (hasIdA && hasIdB) {
      if (Number(idA) !== Number(idB)) {
        return Number(idA) - Number(idB);
      }
    } else if (hasIdA) {
      return -1;
    } else if (hasIdB) {
      return 1;
    }

    const suidA = a.status_unique_id;
    const suidB = b.status_unique_id;
    const hasSuidA = suidA !== null && suidA !== undefined;
    const hasSuidB = suidB !== null && suidB !== undefined;

    if (hasSuidA && hasSuidB) {
      return Number(suidA) - Number(suidB);
    } else if (hasSuidA) {
      return -1;
    } else if (hasSuidB) {
      return 1;
    }

    return 0;
  });

  return ongoingStatuses[0];
};

// ─── Event lead (one lead per event) payload + validation ───────────────────

const isDeadEvent = (event: Pick<IMultipleEvent, "status" | "actual_status">) =>
  (event.actual_status || "").toUpperCase() === "DEAD" ||
  (event.status || "").toUpperCase() === "DEAD";

/**
 * Lead-level part of the upsert payload: the lead's common (customer) fields with
 * every per-event field cleared, so each event row can fill its own values in.
 */
export const buildEventLeadsBasePayload = (
  lead: ILead,
  companyOutlet: {
    company_outlet_uuid?: string | null;
    company_outlet_name?: string | null;
  },
) => ({
  ...lead,
  company_outlet_uuid: companyOutlet.company_outlet_uuid,
  company_outlet_name: companyOutlet.company_outlet_name,
  event_uuid: undefined,
  event_type: undefined,
  event_time: undefined,
  event_time_uuid: undefined,
  preferred_venue: undefined,
  preferred_venue_uuid: undefined,
  status: undefined,
  actual_status: undefined,
  drive_status_uuid: undefined,
  service: undefined,
  number_of_rooms: undefined,
  number_of_guests: undefined,
  payment_mode: undefined,
  cancellation_dead_reason: undefined,
  other_event_name: undefined,
  function_month: undefined,
  function_date: undefined,
  followup_date: undefined,
});

/** One `/leads/upsert-lead` row: lead-level `baseValues` + the event's own fields. */
export const buildEventLeadPayload = (baseValues: any, event: IMultipleEvent) => ({
  ...baseValues,
  // Use individual lead UUID for each event (not URL UUID)
  leads_uuid: event.leads_uuid,
  // Event identification
  event_uuid: event.event_uuid,
  event_type: event.event_type,
  event_time: event.event_time,
  event_time_uuid: event.event_time_uuid,
  preferred_venue: event.preferred_venue,
  preferred_venue_uuid: event.preferred_venue_uuid,
  // Event-specific fields (independent per row)
  function_month: event.function_month,
  function_date: event.function_date,
  // Send null (not "") when no follow-up date is set or status is DEAD.
  followup_date: isDeadEvent(event) ? null : event.followup_date || null,
  status: event.status,
  actual_status: event.actual_status,
  drive_status_uuid: event.drive_status_uuid,
  service: event.service,
  number_of_rooms: event.number_of_rooms,
  number_of_guests: event.number_of_guests,
  customer_relative_name: event.customer_relative_name,
  relation_with_customer: event.relation_with_customer,
  estimated_rough_revenue: event.estimated_rough_revenue ?? null,
  guest_budget: event.guest_budget ?? null,
  // Conditional fields
  payment_mode: event.payment_mode,
  cancellation_dead_reason: event.cancellation_dead_reason,
  other_event_name: event.other_event_name,
  duplicate_reference_uuid: event.duplicate_reference_uuid,
  duplicate_reference_contact_number: event.duplicate_reference_contact_number,
  // Custom Title (key/value pairs) — per-event, not the lead-level extras in baseValues
  extras: event.extras ?? null,

  // Remove multiple_events_list from payload as it's not in swagger
  multiple_events_list: undefined,
  is_archived: event.is_archived,
});

/**
 * Per-event validation shared by the lead form and the "create lead from
 * quotation" dialog. Errors are keyed `multiple_events_list_${index}_${field}`,
 * which is what EventDetailsManager reads.
 */
export const validateEventLeads = (
  events: IMultipleEvent[],
  options: { isLeadDead?: boolean } = {},
): Record<string, string> => {
  const errors: Record<string, string> = {};
  // A DEAD lead/event has no follow-up date
  const hasDeadStatusInLead = options.isLeadDead || events.some(isDeadEvent);

  events.forEach((event, index) => {
    // ========== Required only if active ==========
    if (event.is_archived === 1) return;
    const key = (field: string) => `multiple_events_list_${index}_${field}`;

    // ========== Required Fields (Always) ==========
    if (!event.event_type) {
      errors[key("event_type")] = `Event Type is required`;
    }
    if (!event.actual_status) {
      errors[key("status")] = `Status is required`;
    }
    if (!event.number_of_guests || event.number_of_guests === 0) {
      errors[key("number_of_guests")] = `Number of Guests is required`;
    }

    // ========== Conditional Fields Based on Event Type ==========
    if (event.event_type === "Other Social" && !event.other_event_name) {
      errors[key("other_event_name")] = `Event Name is required`;
    }
    if (event.event_type === "Corporate" && !event.payment_mode) {
      errors[key("payment_mode")] = `Payment Mode is required`;
    }

    // ========== Status-Based Validations ==========
    // "DEAD" // this condition remove as required by Client (The Weding Plenet)
    if (["BOOKED", "HOLD", "CANCELLED"].includes(event.actual_status)) {
      if (!event.preferred_venue) {
        errors[key("preferred_venue")] =
          `Preferred Venue is required when status is ${event.status}`;
      }
      if (!event.function_date) {
        errors[key("function_date")] =
          `Event Date is required when status is ${event.status}`;
      }
      if (!event.event_time) {
        errors[key("event_time")] =
          `Event Time is required when status is ${event.status}`;
      }
    }

    if (
      !hasDeadStatusInLead &&
      !isDeadEvent(event) &&
      ["ONGOING"].includes(event.actual_status || "") &&
      !event.followup_date
    ) {
      errors[key("followup_date")] = `Followup Date is required`;
    }

    if (
      ["CANCELLED", "DEAD"].includes(event.actual_status || "") &&
      !event.cancellation_dead_reason
    ) {
      errors[key("cancellation_dead_reason")] = `Cancellation Reason is required`;
    }

    // ========== Environment Variable Based Validation ==========
    if (
      process.env.REACT_APP_FUNCTION_DATE_MANDATORY === "true" &&
      !event.function_date
    ) {
      errors[key("function_date")] = `Event Date is required`;
    }
  });

  return errors;
};
